import {
  Dialog as MuiDialog,
  DialogContent as MuiDialogContent,
  DialogContentText as MuiDialogContentText,
} from '@mui/material';
import { FC } from 'react';
import { useEscapeKey } from '@csp/csp-web-hooks';
import { LoadingOverlay } from '../progress/LoadingOverlay';
import { AlertError } from '../../lab/alert/AlertError';
import { AlertInfo } from '../../lab/alert/AlertInfo';
import { SxProps } from '../../../common/system/SxProps';
import { Markdown } from '../../markdown/Markdown';
import { Body1 } from '../../typography/TextStyles';
import { DialogHeader } from './DialogHeader';
import { AlertDialogProps } from './model/AlertDialogProps';

export const AlertDialog: FC<AlertDialogProps> = ({
  actions,
  description,
  id,
  onClose,
  isLoading,
  isLoadingInitial,
  loadingInitialText,
  error,
  header,
  children,
  minHeight,
  ariaLabelClose,
  maxHeight,
  scroll = 'body',
  hasMarkdown,
  preventDrawerClose,
  info,
  ...props
}) => {
  const sx: SxProps = {
    minHeight,
    maxHeight,
    justifyContent: 'center',
    px: { xs: 4, sm: 8, md: 12 },
    py: 3,
    position: 'relative',
  };

  const sxDialog: SxProps = {
    '.MuiDialog-paper': {
      width: { xs: '100%', md: '45rem' },
      maxWidth: { md: '45rem' },
    },
  };

  const { ref } = useEscapeKey<HTMLDivElement>(preventDrawerClose ? undefined : onClose);

  return (
    <MuiDialog
      id={id}
      ref={ref}
      keepMounted
      scroll={scroll}
      sx={sxDialog}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      {...props}
    >
      <DialogHeader header={header} onClose={onClose} ariaLabelClose={ariaLabelClose} />
      {isLoading && <LoadingOverlay position="absolute" left="0" isLoading={isLoading} />}

      <MuiDialogContent sx={sx} id="alert-dialog-content">
        <AlertError message={error} mb={children || description || info ? 2 : 0} />
        <AlertInfo message={info} mb={children || description ? 2 : 0} />

        {isLoadingInitial && loadingInitialText && !error && <Body1 component="span">{loadingInitialText}</Body1>}

        {description && (
          <MuiDialogContentText component="span" color="textPrimary" id="alert-dialog-description">
            {hasMarkdown ? (
              <Markdown body="body1" span="body1">
                {description}
              </Markdown>
            ) : (
              description
            )}
          </MuiDialogContentText>
        )}
        {isLoadingInitial && !error && (
          <LoadingOverlay position={loadingInitialText ? 'static' : 'absolute'} left="0" isLoading={isLoadingInitial} />
        )}
        {children}
      </MuiDialogContent>
      {actions}
    </MuiDialog>
  );
};
